import { useEffect, useState } from 'react';
import AuthService from '../../../services/auth_service';
import PermissionService from '../../../services/permission_service';
import {
  canAccessView,
  defaultViewForPermissions,
  getAuthViewFromLocation,
  getViewFromLocation,
  replaceWithLogin,
  syncUrlForAuthView,
  syncUrlForView,
} from '../routes';

const AuthSession = ({ setAuthView, setResetCode, updateMessage, initView }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [permission, setPermission] = useState([]);
  const [activeTab, setActiveTab] = useState('inicio');
  const [loading, setLoading] = useState(true);
  const [init, setInit] = useState(false);

  const setActiveView = (view, replace = false) => {
    setActiveTab(view);
    syncUrlForView(view, replace);
  };

  const setAuthRouteView = (view, replace = false) => {
    setAuthView(view);
    syncUrlForAuthView(view, replace);
  };

  const resolveRequestedView = (permissionCodes) => {
    const requestedView = getViewFromLocation();
    if (requestedView && canAccessView(requestedView, permissionCodes)) return requestedView;
    return defaultViewForPermissions(permissionCodes);
  };

  const loadUserFromToken = async ({ preserveAuthRoute = false } = {}) => {
    try {
      const user = await AuthService.getContactFromToken();
      setCurrentUser(user);
      if (user) {
        const permissions = await PermissionService.getByRole(user.FK_Role);
        const permissionCodes = permissions.map((p) => Number(p.Code));
        setPermission(permissionCodes);
        const targetView = resolveRequestedView(permissionCodes);
        setActiveView(targetView, true);
      }
    } catch (err) {
      setCurrentUser(null);
      setPermission([]);
      setActiveTab('inicio');
      if (!preserveAuthRoute) {
        replaceWithLogin();
      }
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionCode = params.get('session');
    const resetToken = params.get('token');
    if (!currentUser && sessionCode && resetToken) {
      setAuthRouteView('resetPassword', true);
      setResetCode({ session: sessionCode, token: resetToken });
      setLoading(false);
      return;
    }
    const authViewFromUrl = getAuthViewFromLocation();
    if (!currentUser && authViewFromUrl) {
      setAuthView(authViewFromUrl);
      loadUserFromToken({ preserveAuthRoute: true }).finally(() => setLoading(false));
      return;
    }
    loadUserFromToken().finally(() => setLoading(false));
  }, []);

  const handleLogin = async (resetSessionExpiredFlag) => {
    setLoading(true);
    try {
      resetSessionExpiredFlag();
      await loadUserFromToken();
      setAuthView('login');
      setInit(true);
    } catch (e) {
      setCurrentUser(null);
    } finally {
      setLoading(false);
    }
  };

  const clearClientSession = (resetSessionExpiredFlag) => {
    resetSessionExpiredFlag();
    setCurrentUser(null);
    setActiveTab('inicio');
    setAuthRouteView('login', true);
    setResetCode(null);
    setPermission([]);
    setInit(false);
    replaceWithLogin();
  };

  const handleLogout = async (resetSessionExpiredFlag, forceLocal = false) => {
    try {
      const confirm = await AuthService.logout(currentUser?.PrincipalEmail);
      if (confirm === true || confirm === 'unauthorized' || forceLocal) {
        clearClientSession(resetSessionExpiredFlag);
      }
    } catch (e) {
      if (forceLocal) {
        clearClientSession(resetSessionExpiredFlag);
        return;
      }
      throw e;
    }
  };

  useEffect(() => {
    const onPopState = () => {
      if (!currentUser) {
        replaceWithLogin();
        setAuthView('login');
        return;
      }
      const requestedView = getViewFromLocation();
      if (requestedView && canAccessView(requestedView, permission)) {
        setActiveTab(requestedView);
        return;
      }
      setActiveView(defaultViewForPermissions(permission), true);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [currentUser, permission]);

  const closeSession = async (resetSessionExpiredFlag) => {
    updateMessage('Sesión cerrada', 'info');
    await handleLogout(resetSessionExpiredFlag);
  };

  return {
    init,
    loading,
    currentUser,
    permission,
    activeTab,
    setCurrentUser,
    setActiveTab: setActiveView,
    setAuthView: setAuthRouteView,
    loadUserFromToken,
    handleLogin,
    handleLogout,
    closeSession
  };
};

export default AuthSession;
